import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, formatNum, monthStr, monthLabel } from "@/lib/format";
import { SectionTitle, StatusBadge } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Target, Save, Plus, Trash2, Copy, History } from "lucide-react";

const KPI_DEFS = [
  { key: "revenue", label: "Revenue", type: "currency" },
  { key: "calls", label: "Calls", type: "number" },
  { key: "visits", label: "Visits", type: "number" },
  { key: "leads", label: "Leads", type: "number" },
  { key: "treatments", label: "Treatments", type: "number" },
  { key: "conversion", label: "Treatment Conversion %", type: "percent" },
];
const QUAL_STATUS = ["Not started", "In progress", "Done"];

export default function Goals() {
  const { telecallers } = useApp();
  const [month, setMonth] = useState(monthStr());
  const [goals, setGoals] = useState({ team_goals: {}, employee_targets: [], qualitative: [] });
  const [actuals, setActuals] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.getGoals(month).then((g) => setGoals({
      team_goals: g.team_goals || {}, employee_targets: g.employee_targets || [],
      qualitative: g.qualitative || [], version: g.version,
    }));
    api.goalsActuals(month).then(setActuals);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const setTeam = (k, v) => setGoals((g) => ({ ...g, team_goals: { ...g.team_goals, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.saveGoals({ month, ...goals }); toast.success("Goals saved"); load(); }
    catch { toast.error("Could not save goals"); } finally { setSaving(false); }
  };

  const copyPrev = () => {
    const [y, m] = month.split("-").map(Number);
    const pm = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
    api.getGoals(pm).then((g) => {
      setGoals({ team_goals: g.team_goals || {}, employee_targets: g.employee_targets || [], qualitative: g.qualitative || [] });
      toast.success(`Copied from ${monthLabel(pm)} — remember to Save`);
    });
  };

  const empTarget = (empId) => goals.employee_targets.find((t) => t.employee_id === empId) || {};
  const setEmpTarget = (empId, k, v) => setGoals((g) => {
    const arr = [...g.employee_targets];
    const idx = arr.findIndex((t) => t.employee_id === empId);
    if (idx >= 0) arr[idx] = { ...arr[idx], [k]: v }; else arr.push({ employee_id: empId, [k]: v });
    return { ...g, employee_targets: arr };
  });

  const addQual = () => setGoals((g) => ({ ...g, qualitative: [...g.qualitative, { text: "", status: "Not started", note: "" }] }));
  const setQual = (i, patch) => setGoals((g) => { const arr = [...g.qualitative]; arr[i] = { ...arr[i], ...patch }; return { ...g, qualitative: arr }; });
  const delQual = (i) => setGoals((g) => ({ ...g, qualitative: g.qualitative.filter((_, x) => x !== i) }));

  return (
    <div className="pb-24 animate-fade-up">
      <SectionTitle icon={Target} title="Monthly Goals" desc="Set targets · tracked live from entries"
        right={
          <div className="flex items-center gap-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg w-40" data-testid="goals-month" />
            <Button variant="outline" size="sm" className="rounded-lg" onClick={copyPrev} data-testid="copy-prev-btn"><Copy className="h-4 w-4 mr-1.5" /> Copy last month</Button>
          </div>
        } />
      {goals.version > 0 && <div className="text-xs text-slate-500 mb-4 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Version {goals.version} · {monthLabel(month)}</div>}

      <div className="surface p-5 mb-6">
        <div className="font-heading text-lg font-light text-white mb-4">Team Goals — Target vs Actual</div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="team-goals-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-4">KPI</th><th className="py-2 px-4">Target</th><th className="py-2 px-4 text-right">Actual</th>
                <th className="py-2 px-4 text-right">Achievement</th><th className="py-2 px-4 text-right">Gap</th><th className="py-2 pl-4 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {KPI_DEFS.map((k) => {
                const target = Number(goals.team_goals[k.key] || 0);
                const actual = Number(actuals[k.key] || 0);
                const ach = target ? Math.round((actual / target) * 1000) / 10 : 0;
                const gap = target - actual;
                const fmt = (v) => (k.type === "currency" ? formatINR(v) : k.type === "percent" ? `${v}%` : formatNum(v));
                return (
                  <tr key={k.key} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 font-semibold text-slate-200">{k.label}</td>
                    <td className="py-2.5 px-4"><Input type="number" value={goals.team_goals[k.key] || ""} onChange={(e) => setTeam(k.key, parseInt(e.target.value || "0", 10))} className="h-8 w-28 rounded-lg font-mono" data-testid={`team-target-${k.key}`} /></td>
                    <td className="py-2.5 px-4 text-right font-mono text-white">{fmt(actual)}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-bold text-white">{ach}%</td>
                    <td className={`py-2.5 px-4 text-right font-mono ${gap > 0 ? "text-rose-400" : "text-emerald-400"}`}>{gap > 0 ? fmt(gap) : "—"}</td>
                    <td className="py-2.5 pl-4 text-right"><StatusBadge pct={ach} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="surface p-5 mb-6">
        <div className="font-heading text-lg font-light text-white mb-4">Per-Employee Targets ({monthLabel(month)})</div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="emp-targets-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-4">Employee</th><th className="py-2 px-3">Revenue</th><th className="py-2 px-3">Calls</th>
                <th className="py-2 px-3">Visits</th><th className="py-2 px-3">Leads</th><th className="py-2 px-3">Treatments</th>
              </tr>
            </thead>
            <tbody>
              {telecallers.map((e) => {
                const t = empTarget(e.id);
                return (
                  <tr key={e.id} className="border-b border-white/5">
                    <td className="py-2 pr-4 font-semibold text-slate-200">{e.name}</td>
                    {["revenue", "calls", "visits", "leads", "treatments"].map((f) => (
                      <td key={f} className="py-2 px-3"><Input type="number" value={t[f] || ""} onChange={(ev) => setEmpTarget(e.id, f, parseInt(ev.target.value || "0", 10))} className="h-8 w-24 rounded-lg font-mono" data-testid={`emp-target-${e.id}-${f}`} /></td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="surface p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-light text-white">Qualitative Goals</div>
          <Button variant="outline" size="sm" className="rounded-lg" onClick={addQual} data-testid="add-qual-btn"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
        </div>
        <div className="space-y-3">
          {goals.qualitative.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No qualitative goals yet — e.g. Follow-up gap zero</div>}
          {goals.qualitative.map((q, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center surface-2 p-3" data-testid={`qual-row-${i}`}>
              <Input placeholder="Goal (e.g. Lost leads daily review)" value={q.text} onChange={(e) => setQual(i, { text: e.target.value })} className="sm:col-span-4 rounded-lg" />
              <Select value={q.status} onValueChange={(v) => setQual(i, { status: v })}>
                <SelectTrigger className="sm:col-span-3 rounded-lg" data-testid={`qual-status-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{QUAL_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Achieved note" value={q.note} onChange={(e) => setQual(i, { note: e.target.value })} className="sm:col-span-4 rounded-lg" />
              <button onClick={() => delQual(i)} className="sm:col-span-1 text-rose-400 hover:text-rose-300 flex justify-center" data-testid={`qual-del-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#0b1120]/95 backdrop-blur-md border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-4 py-3 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120] h-11 px-6 font-semibold" data-testid="save-goals-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Goals"}
          </Button>
        </div>
      </div>
    </div>
  );
}
